import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
	Typography,
	Tag,
	Button,
	Space,
	Tooltip,
	Badge,
	Segmented,
	Popconfirm,
} from "antd";
import {
	ReloadOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
	StopOutlined,
	ExclamationCircleOutlined,
	ExclamationCircleFilled,
	PlusOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";



import Layout from "../../../components/Layout";
import {PageContainer, DataTable, StatusTag} from "../../../components/shared";
import PageToolbar from "../../../components/shared/PageToolbar";

import {
	fetchCompanyPlans,
	pausePlan,
	resumePlan,
	cancelPlan,
} from "../../../api/healthcare/TreatmentPlanApi";
import { fetchPatientsByIds } from "../../../api/healthcare/PatientApi";

import {
	PatientTreatmentPlan,
	STAGE_CONFIG,
	StageType,
} from "../../../types/healthcare/TreatmentPlanTypes";
import { User } from "../../../types/admin/UserTypes";
import { useNotifications } from "../../../context/NotificationContext";
import EditPatientModal from "../patient/PatientModal";
import { Patient } from "../../../types/healthcare/PatientTypes";
import PlanDetailDrawer from "./planComponents/PlanDetailDrawer";
import CreateTreatmentPlanModal from "./planComponents/CreateTreatmentPlanModal";

dayjs.extend(relativeTime);
const { Text } = Typography;

/* ─── Types ───────────────────────────────────────────────────────────────── */

type ActionRequired =
	| "manual_advance"
	| "practitioner_task"
	| "patient_analysis"
	| "sub_plan_selection"
	| null;

interface PlanRow extends PatientTreatmentPlan {
	_actionRequired: ActionRequired;
	_waitingFor: string | null;
	_elapsed: string;
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function deriveActionRequired(plan: PatientTreatmentPlan): ActionRequired {
	if (!plan.stage_logs) return null;
	for (const log of plan.stage_logs) {
		if (log.status === "waiting" || log.status === "in_progress") {
			const awaiting = (log.outcome_data as any)?.awaiting_action;
			if (awaiting === "sub_plan_selection") return "sub_plan_selection";
			if (awaiting === "practitioner_review") return "manual_advance";
			if (log.stage?.stage_type === "practitioner_task") return "practitioner_task";
			if (log.stage?.stage_type === "patient_analysis") return "patient_analysis";
		}
	}
	return null;
}

function deriveWaitingFor(plan: PatientTreatmentPlan): string | null {
	const stage = plan.current_stage;
	if (!stage) return null;
	const cfg = STAGE_CONFIG[stage.stage_type as StageType];
	return cfg ? `${cfg.label}: ${stage.label}` : stage.label;
}

function toRows(plans: PatientTreatmentPlan[]): PlanRow[] {
	return plans.map((p) => ({
		...p,
		_actionRequired: deriveActionRequired(p),
		_waitingFor: deriveWaitingFor(p),
		_elapsed: p.started_at ? dayjs(p.started_at).fromNow(true) : "—",
	}));
}

/* ─── Action badge ────────────────────────────────────────────────────────── */

const ActionBadge: React.FC<{ type: ActionRequired }> = ({ type }) => {
	if (!type) return null;

	const config: Record<NonNullable<ActionRequired>, { label: string; color: string; bg: string }> = {
		manual_advance:     { label: "Review Required", color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
		practitioner_task:  { label: "Task Pending",    color: "#7c3aed", bg: "rgba(124,58,237,0.1)" },
		patient_analysis:   { label: "Analysis Ready",  color: "#0d9488", bg: "rgba(13,148,136,0.1)" },
		sub_plan_selection: { label: "Plan Selection",  color: "#ec4899", bg: "rgba(236,72,153,0.1)" },
	};

	const { label, color, bg } = config[type];

	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 4,
				padding: "2px 8px",
				borderRadius: 20,
				background: bg,
				border: `1px solid ${color}44`,
				fontSize: 11,
				fontWeight: 600,
				color,
				whiteSpace: "nowrap",
			}}
		>
			<ExclamationCircleOutlined style={{ fontSize: 11 }} />
			{label}
		</span>
	);
};

/* ─── Constants ───────────────────────────────────────────────────────────── */

const CATEGORY_OPTIONS = [
	{ label: "All Categories", value: "" },
	{ label: "Peptides", value: "Peptides" },
	{ label: "Genetics", value: "Genetics" },
	{ label: "Wellness", value: "Wellness" },
	{ label: "Supplements", value: "Supplements" },
];

/* ─── Page ────────────────────────────────────────────────────────────────── */

const TreatmentPlanDashboardPage: React.FC = () => {
	const { showSuccess, showError } = useNotifications();

	/* ── List state ──────────────────────────────────────────────────────── */
	const [rows, setRows] = useState<PlanRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const PER_PAGE = 25;

	const [statusFilter, setStatusFilter] = useState("");
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [actionFilter, setActionFilter] = useState(false);

	/* ── Detail drawer state ─────────────────────────────────────────────── */
	const [detailPlan, setDetailPlan] = useState<PlanRow | null>(null);

	/* ── Patient record modal ────────────────────────────────────────────── */
	const [patientModal, setPatientModal] = useState<Patient | null>(null);

	/* ── Create treatment plan modal ─────────────────────────────────────── */
	const [createModalOpen, setCreateModalOpen] = useState(false);

	/* ── Load list ───────────────────────────────────────────────────────── */
	const load = useCallback(async (p = page, status = statusFilter) => {
		setLoading(true);
		try {
			const res = await fetchCompanyPlans({
				status: status || undefined,
				page: p,
				per_page: PER_PAGE,
			});
			const plans = res.data;
			setTotal(res.total);

			const patientIds = [...new Set(plans.map((pl) => pl.patient_id))];
			const patients = patientIds.length > 0 ? await fetchPatientsByIds(patientIds) : [];
			const patientMap = new Map(patients.map((pt) => [pt.id, pt]));

			const enriched = plans.map((pl) => ({
				...pl,
				// Prefer patient already embedded by API, fall back to bulk fetch result
				patient: (pl as any).patient ?? patientMap.get(pl.patient_id) ?? null,
			}));
			setRows(toRows(enriched as any[]));
		} catch {
			showError("Failed to load treatment plans");
		} finally {
			setLoading(false);
		}
	}, [page, statusFilter]);

	useEffect(() => { load(page, statusFilter); }, [page, statusFilter]);

	/* ── Table row actions (pause/resume/cancel without opening drawer) ─── */
	const handlePause = useCallback(async (plan: PatientTreatmentPlan) => {
		try {
			await pausePlan(plan.id);
			showSuccess("Plan paused");
			load();
			if (detailPlan?.id === plan.id) setDetailPlan((prev) => prev ? { ...prev, status: "paused" } : null);
		} catch { showError("Failed to pause plan"); }
	}, [load, detailPlan]);

	const handleResume = useCallback(async (plan: PatientTreatmentPlan) => {
		try {
			await resumePlan(plan.id);
			showSuccess("Plan resumed");
			load();
			if (detailPlan?.id === plan.id) setDetailPlan((prev) => prev ? { ...prev, status: "active" } : null);
		} catch { showError("Failed to resume plan"); }
	}, [load, detailPlan]);

	const handleCancel = useCallback(async (plan: PatientTreatmentPlan) => {
		try {
			await cancelPlan(plan.id);
			showSuccess("Plan cancelled");
			if (detailPlan?.id === plan.id) setDetailPlan(null);
			load();
		} catch { showError("Failed to cancel plan"); }
	}, [load, detailPlan]);

	/* ─── Derived stats ──────────────────────────────────────────────────── */
	const stats = useMemo(() => ({
		active:      rows.filter((r) => r.status === "active").length,
		paused:      rows.filter((r) => r.status === "paused").length,
		needsAction: rows.filter((r) => r._actionRequired !== null).length,
		completed:   rows.filter((r) => r.status === "completed").length,
	}), [rows]);

	/* ─── Local filter ───────────────────────────────────────────────────── */
	const filtered = useMemo(() => {
		let r = rows;
		if (search.trim()) {
			const q = search.toLowerCase();
			r = r.filter((p) => {
				const pt = (p as any).patient;
				const name = pt ? `${pt.first_name} ${pt.last_name}`.toLowerCase() : "";
				return name.includes(q) || p.template?.name?.toLowerCase().includes(q);
			});
		}
		if (categoryFilter) r = r.filter((p) => p.template?.category === categoryFilter);
		if (actionFilter)    r = r.filter((p) => p._actionRequired !== null);
		return r;
	}, [rows, search, categoryFilter, actionFilter]);

	/* ─── Columns ────────────────────────────────────────────────────────── */
	const columns: ColumnsType<PlanRow> = [
		{
			title: "Patient",
			width: 180,
			render: (_, r) => {
				const pt = (r as any).patient;
				const name = pt ? `${pt.first_name} ${pt.last_name}`.trim() : null;
				return name ? (
					<Button
						type="link"
						style={{ padding: 0, height: "auto", textAlign: "left" }}
						onClick={(e) => { e.stopPropagation(); setPatientModal(pt); }}
					>
						<Text strong style={{ fontSize: 13 }}>{name}</Text>
					</Button>
				) : (
					<Text type="secondary" style={{ fontSize: 12 }}>Patient #{r.patient_id}</Text>
				);
			},
		},
		{
			title: "Treatment Plan",
			render: (_, r) => (
				<div>
					<Text strong style={{ fontSize: 13 }}>
						{r.template?.name ?? `Plan #${r.id}`}
					</Text>
					{r.template?.category && (
						<Tag
							style={{
								marginLeft: 6,
								fontSize: 10,
								lineHeight: "16px",
								background: "var(--mg-primary-bg, rgba(59,130,246,.08))",
								color: "var(--mg-primary)",
								border: "1px solid var(--mg-primary-border, rgba(59,130,246,.2))",
							}}
						>
							{r.template.category}
						</Tag>
					)}
					{r._actionRequired && (
						<div style={{ marginTop: 4 }}>
							<ActionBadge type={r._actionRequired} />
						</div>
					)}
				</div>
			),
		},
		{
			title: "Status",
			width: 110,
			render: (_, r) => (
				<StatusTag status={r.status} label={r.status.charAt(0).toUpperCase() + r.status.slice(1)} />
			),
		},
		{
			title: "Current Stage",
			width: 220,
			render: (_, r) => {
				if (!r.current_stage) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
				const cfg = STAGE_CONFIG[r.current_stage.stage_type as StageType];
				return (
					<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<Badge color={cfg?.color ?? "#999"} />
						<Text style={{ fontSize: 12 }}>{r.current_stage.label}</Text>
					</div>
				);
			},
		},
		{
			title: "Started",
			width: 110,
			dataIndex: "started_at",
			render: (v) =>
				v ? (
					<Tooltip title={dayjs(v).format("D MMM YYYY HH:mm")}>
						<Text type="secondary" style={{ fontSize: 12 }}>{dayjs(v).fromNow()}</Text>
					</Tooltip>
				) : "—",
		},
		{
			title: "",
			width: 90,
			render: (_, r) => (
				<Space size={4} onClick={(e) => e.stopPropagation()}>
					{r.status === "active" && (
						<Tooltip title="Pause plan">
							<Button size="small" icon={<PauseCircleOutlined />} onClick={() => handlePause(r)} />
						</Tooltip>
					)}
					{r.status === "paused" && (
						<Tooltip title="Resume plan">
							<Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={() => handleResume(r)} />
						</Tooltip>
					)}
					{(r.status === "active" || r.status === "paused") && (
						<Popconfirm
							title="Cancel this plan?"
							description="This action cannot be undone."
							onConfirm={() => handleCancel(r)}
							okText="Cancel Plan"
							okType="danger"
							cancelText="Keep"
							icon={<ExclamationCircleFilled style={{ color: "#ff4d4f" }} />}
						>
							<Tooltip title="Cancel plan">
								<Button size="small" danger icon={<StopOutlined />} />
							</Tooltip>
						</Popconfirm>
					)}
				</Space>
			),
		},
	];

	

	/* ─── Render ─────────────────────────────────────────────────────────── */
	return (
		<Layout>
			<PageContainer
				data-testid="treatment-plan-root"
			>

				{/* ── Toolbar ─────────────────────────────────────────────────── */}
				<PageToolbar
					primaryAction={{ label: "New Treatment Plan", icon: <PlusOutlined />, onClick: () => setCreateModalOpen(true), testId: "treatment-plan-create-btn" }}
					extraActions={[
						{ label: "Refresh", icon: <ReloadOutlined />, onClick: () => load(1, statusFilter) },
					]}
					filters={[
						{
							type: "custom",
							render: (
								<Segmented
									value={actionFilter ? "__action__" : statusFilter}
									onChange={(val) => {
										if (val === "__action__") {
											setActionFilter(true);
											setStatusFilter("");
										} else {
											setActionFilter(false);
											setStatusFilter(val as string);
											setPage(1);
										}
									}}
									options={[
										{ label: `All${total ? ` ${total}` : ""}`, value: "" },
										{ label: `Active${stats.active ? ` ${stats.active}` : ""}`, value: "active" },
										{ label: `Paused${stats.paused ? ` ${stats.paused}` : ""}`, value: "paused" },
										{ label: `Completed${stats.completed ? ` ${stats.completed}` : ""}`, value: "completed" },
										{ label: `Cancelled`, value: "cancelled" },
										{
											label: (
												<span>
													⚠ Needs Action{" "}
													{stats.needsAction > 0 && (
														<Badge
															count={stats.needsAction}
															style={{ marginLeft: 4, fontSize: 10, backgroundColor: "#f59e0b" }}
														/>
													)}
												</span>
											),
											value: "__action__",
										},
									]}
								/>
							),
						},
						{
							type: "search",
							placeholder: "Search patient or plan…",
							value: search,
							onChange: (v) => setSearch(v),
							width: 220,
						},
						{
							type: "select",
							options: CATEGORY_OPTIONS,
							value: categoryFilter,
							onChange: (v) => setCategoryFilter(v),
							width: 160,
							placeholder: "All Categories",
						},
					]}
					onReset={() => {
						setSearch("");
						setCategoryFilter("");
						setStatusFilter("");
						setActionFilter(false);
						load(1, "");
					}}
				/>

				{/* ── Main table ──────────────────────────────────────────────── */}
				<DataTable
					columns={columns}
					dataSource={filtered}
					rowKey="id"
					loading={loading}
					total={total}
					currentPage={page}
					pageSize={PER_PAGE}
					onPageChange={(p) => { setPage(p); load(p, statusFilter); }}
					onRowClick={(r) => setDetailPlan(r)}
					emptyTitle="No treatment plans found"
					rowClassName={(r) => r._actionRequired ? "mg-row--needs-action" : ""}
				/>
			</PageContainer>

			{/* ── Plan detail drawer ───────────────────────────────────────── */}
			{detailPlan && (
				<PlanDetailDrawer
					plan={detailPlan}
					onClose={() => setDetailPlan(null)}
					onPlanChange={() => load()}
				/>
			)}

			{/* ── Patient record modal ─────────────────────────────────────── */}
			{patientModal && (
				<EditPatientModal
					isOpen
					patient={patientModal}
					onClose={() => setPatientModal(null)}
				/>
			)}

			{/* ── Create treatment plan modal ───────────────────────────────── */}
			<CreateTreatmentPlanModal
				open={createModalOpen}
				onClose={() => setCreateModalOpen(false)}
				onCreated={() => load(1, statusFilter)}
			/>

			<style>{`
				.mg-row--needs-action td { background: rgba(245,158,11,0.04) !important; }
				.mg-row--needs-action:hover td { background: rgba(245,158,11,0.08) !important; }
				tr.ant-table-row { cursor: pointer; }
			`}</style>
		</Layout>
	);
};

export default TreatmentPlanDashboardPage;
